import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, X, ArrowRight, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuthStore } from '@/features/auth/authStore';
import { ONBOARDING_STEPS, useOnboardingStatus } from './hooks/useOnboarding';
import { cn } from '@/lib/utils';

const hiddenKey = (userId: string | undefined) => `omnibiz-setup-checklist-hidden:${userId ?? 'anonymous'}`;

function readHidden(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeHidden(key: string) {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // ignore
  }
}

export function SetupChecklist() {
  const userId = useAuthStore((s) => s.user?.id);
  const key = hiddenKey(userId);
  const [hidden, setHidden] = useState(() => readHidden(key));
  const { data } = useOnboardingStatus();

  if (!data || hidden) return null;
  const doneCount = ONBOARDING_STEPS.filter((s) => data.steps[s.key]).length;
  if (doneCount === ONBOARDING_STEPS.length) return null;

  const hide = () => {
    writeHidden(key);
    setHidden(true);
  };

  const progressPercent = Math.round((doneCount / ONBOARDING_STEPS.length) * 100);

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      {/* Header Strip */}
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Sparkles className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Quick Setup Guide
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {doneCount} of {ONBOARDING_STEPS.length} completed
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Mini progress bar */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">{progressPercent}%</span>
          </div>

          <button
            type="button"
            onClick={hide}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            aria-label="Hide setup checklist"
            title="Dismiss setup guide"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Step Capsules */}
      <div className="grid grid-cols-1 gap-2 p-2.5 sm:p-3 sm:grid-cols-2 lg:grid-cols-4 bg-white/70 dark:bg-slate-900/70">
        {ONBOARDING_STEPS.map((s, idx) => {
          const done = data.steps[s.key];
          return (
            <Link
              key={s.key}
              to={`/onboarding/${s.key}`}
              className={cn(
                'group relative flex items-center justify-between gap-2 rounded-xl border p-2.5 text-xs font-semibold transition-all duration-150',
                done
                  ? 'border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/50'
                  : 'border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-850 text-slate-700 dark:text-slate-200 hover:border-primary/40 dark:hover:border-primary/40 hover:shadow-xs'
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-bold',
                    done
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-primary/10 group-hover:text-primary'
                  )}
                >
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx + 1}
                </span>
                <span className={cn('truncate text-[11px]', done ? 'text-slate-600 dark:text-slate-400 font-medium' : 'text-slate-800 dark:text-slate-200')}>
                  {s.checklistLabel}
                </span>
              </div>
              <ArrowRight className="h-3 w-3 shrink-0 text-slate-300 dark:text-slate-600 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
